import React, { useEffect, useState } from 'react'
import { useUser } from '../../context/UserContext';
import { useOrganization } from '../../context/OrganizationContext';
import { AdvancedButton } from '../AdvancedButton';
import { integrationOauth, syncAccountingCustomers } from '../../utils/api';
import useQueryParam from '../../hooks/useQueryParams';
import { useNavigate, useLocation } from 'react-router-dom';
import { QUERY_PARAMS } from '../../utils/shared/constants';
import { useText } from '../../context/TextContext';

export default function AccountingTab() {
    const { getToken } = useUser();
    const { orgId, organization } = useOrganization();
    const text = useText();
    const { code } = useQueryParam();
    const navigate = useNavigate();
    const location = useLocation();
    const [processing, setProcessing] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<{ imported: number; linked: number; pushed: number } | null>(null);
    const [error, setError] = useState<string | null>(null);

    const isConnected = !!organization.integrations?.accountingType;

    useEffect(() => {
        if (!code) return;

        const handleCallback = async () => {
            setProcessing(true);
            setError(null);
            try {
                const token = await getToken();
                await integrationOauth(token, orgId, 'fortnox', code);
                // Remove code from URL after successful exchange
                const params = new URLSearchParams(location.search);
                params.delete(QUERY_PARAMS.CODE);
                navigate(`${location.pathname}?${params.toString()}`, { replace: true });
            } catch (e: any) {
                console.error('Failed to exchange Fortnox OAuth code', e);
            } finally {
                setProcessing(false);
            }
        };

        handleCallback();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    const handleConnect = async () => {
        setProcessing(true);
        setError(null);
        try {
            const token = await getToken();
            const { redirectUrl } = await integrationOauth(token, orgId, 'fortnox');
            if (redirectUrl) {
                window.location.href = redirectUrl;
            }
        } catch (e: any) {
            setError(e.message || text.failedToInitiateFortnoxConnection);
            setProcessing(false);
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        setSyncResult(null);
        setError(null);
        try {
            const token = await getToken();
            const result = await syncAccountingCustomers(token, orgId);
            setSyncResult(result);
        } catch (e: any) {
            setError(e.message || text.failedToSyncCustomers);
        } finally {
            setSyncing(false);
        }
    };

    // if (!claims.superAdmin) {
    //     return (
    //         <div>
    //             <p className='text-muted'>{text.comingSoon}</p>
    //         </div>
    //     );
    // }

    return (
        <div className='d-flex flex-column gap-3'>
            {isConnected ? (
                <>
                    <p className='text-success m-0'>{text.fortnoxConnected}</p>
                    <div>
                        <AdvancedButton onClick={handleSync} processing={syncing}>
                            {text.syncCustomers}
                        </AdvancedButton>
                    </div>
                    {syncResult && (
                        <p className='text-muted m-0 small'>
                            {text.syncCustomersResult
                                .replace('{imported}', String(syncResult.imported))
                                .replace('{linked}', String(syncResult.linked))
                                .replace('{pushed}', String(syncResult.pushed))}
                        </p>
                    )}
                </>
            ) : (
                <>
                    <p className='text-muted m-0'>{text.fortnoxDescription}</p>
                    <div>
                        <AdvancedButton onClick={handleConnect} processing={processing}>
                            {text.connectFortnox}
                        </AdvancedButton>
                    </div>
                </>
            )}
            {error && <p className='text-danger m-0'>{error}</p>}
        </div>
    );
}
